import React, { useState, useEffect } from "react";
import Modal from './Modal'
import { FaPlus, FaStar, FaPhone, FaEnvelope, FaMapMarkerAlt, FaEdit, FaTrash, FaUser, FaBuilding } from "react-icons/fa";
import { Search, RefreshCw } from "lucide-react";
import { API_BASE } from '../config'
import { detectPhoneRegion, getPhoneExample, getPhonePrefix, getPhoneValidationMessage, normalizeEmail, normalizePhone, validateBusinessName, validateEmail, validatePersonName, validatePhone } from '../utils/validation'

const CATEGORIES = [
  "ELECTRICIAN", "PLUMBER", "HOUSE CLEANING", "CARPENTER",
  "PAINTER", "PEST CONTROL", "AC SERVICE", "APPLIANCE REPAIR",
  "GARDENER", "SECURITY", "MOVING", "OTHER"
];

function StarRating({ rating = 0, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <FaStar key={s} size={size} className={s <= Math.round(rating) ? "text-yellow-400" : "text-gray-200"} />
      ))}
      {rating > 0 && <span className="text-xs text-gray-500 ml-1">{Number(rating).toFixed(1)}</span>}
    </div>
  );
}

export default function PartnersManagement() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const communityId = sessionStorage.getItem("community_id") || "";
  const idToken = sessionStorage.getItem("id_token") || "";

  async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json", ...opts.headers },
    });
    if (!resp.ok) throw new Error(`API ${resp.status}`);
    return resp.json();
  }

  async function loadPartners() {
    if (!communityId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/partners?communityId=${encodeURIComponent(communityId)}`);
      setPartners(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("loadPartners error", e);
    }
    setLoading(false);
  }

  useEffect(() => { loadPartners(); }, [communityId]);

  async function handleSave(form) {
    try {
      if (editPartner) {
        await apiFetch("/partners", {
          method: "PATCH",
          body: JSON.stringify({ COMMUNITY_ID: communityId, PARTNER_ID: editPartner.PARTNER_ID, ...form }),
        });
      } else {
        await apiFetch("/partners", {
          method: "POST",
          body: JSON.stringify({ COMMUNITY_ID: communityId, ...form }),
        });
      }
      setShowForm(false);
      setEditPartner(null);
      loadPartners();
    } catch (e) {
      alert("Failed to save partner");
    }
  }

  async function handleDelete(partner) {
    if (!window.confirm(`Delete "${partner.NAME}"?`)) return;
    try {
      await apiFetch(`/partners?communityId=${encodeURIComponent(communityId)}&partnerId=${encodeURIComponent(partner.PARTNER_ID)}`, { method: "DELETE" });
      loadPartners();
    } catch (e) {
      alert("Failed to delete partner");
    }
  }

  const filtered = partners.filter((p) => {
    const matchSearch = !searchTerm || (p.NAME || "").toLowerCase().includes(searchTerm.toLowerCase()) || (p.CATEGORY || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filterCategory === "all" || (p.CATEGORY || "").toUpperCase() === filterCategory;
    return matchSearch && matchCat;
  });

  return (
    <div className="p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold truncate">Service Partners</h2>
          <p className="text-xs text-gray-500 truncate">Manage verified contractors and service providers</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button onClick={loadPartners} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={() => { setEditPartner(null); setShowForm(true); }}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium active:bg-blue-700 flex-1 sm:flex-none"
          >
            <FaPlus size={12} /> Add Partner
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search partners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="w-full sm:w-auto border rounded-lg px-2 py-2 text-sm bg-white"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>
          ))}
        </select>
      </div>

      {/* Partner list */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FaBuilding size={32} className="mx-auto mb-2 text-gray-300" />
          <p>No partners found</p>
          <p className="text-xs mt-1">Add your first service partner</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <div key={p.PARTNER_ID} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${p.TYPE === "COMPANY" ? "bg-blue-100 text-blue-600" : "bg-blue-100 text-blue-600"}`}>
                    {p.TYPE === "COMPANY" ? <FaBuilding size={16} /> : <FaUser size={16} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm truncate">{p.NAME}</h3>
                    <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full mt-0.5">
                      {(p.CATEGORY || "").charAt(0) + (p.CATEGORY || "").slice(1).toLowerCase()}
                    </span>
                    <StarRating rating={parseFloat(p.AVG_RATING || "0")} />
                    <span className="text-xs text-gray-400 ml-1">({p.TOTAL_REVIEWS || 0} reviews)</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditPartner(p); setShowForm(true); }} className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                    <FaEdit size={14} />
                  </button>
                  <button onClick={() => handleDelete(p)} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50">
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
              {/* Contact details */}
              <div className="mt-2 space-y-1 text-xs text-gray-500">
                {p.PHONE && (
                  <div className="flex items-center gap-2">
                    <FaPhone size={10} className="text-gray-400" />
                    <a href={`tel:${p.PHONE}`} className="text-blue-600">{p.PHONE}</a>
                  </div>
                )}
                {p.EMAIL && (
                  <div className="flex items-center gap-2">
                    <FaEnvelope size={10} className="text-gray-400" />
                    <span className="truncate">{p.EMAIL}</span>
                  </div>
                )}
                {p.ADDRESS && (
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt size={10} className="text-gray-400" />
                    <span className="truncate">{p.ADDRESS}</span>
                  </div>
                )}
                {p.DESCRIPTION && <p className="text-gray-600 mt-1">{p.DESCRIPTION}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <PartnerForm
          partner={editPartner}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditPartner(null); }}
        />
      )}
    </div>
  );
}

function PartnerForm({ partner, onSave, onClose }) {
  const phoneRegion = detectPhoneRegion();
  const defaultPrefix = getPhonePrefix(phoneRegion);
  const phoneExample = getPhoneExample(phoneRegion);
  const [form, setForm] = useState({
    NAME: partner?.NAME || "",
    CATEGORY: partner?.CATEGORY || "",
    TYPE: partner?.TYPE || "PERSON",
    PHONE: partner?.PHONE || defaultPrefix,
    EMAIL: partner?.EMAIL || "",
    ADDRESS: partner?.ADDRESS || "",
    DESCRIPTION: partner?.DESCRIPTION || "",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    setPhoneError("");
    if (!form.CATEGORY) {
      setFormError('Please select a category');
      return;
    }
    if (form.TYPE === 'PERSON' && !validatePersonName(form.NAME)) {
      setFormError('Please enter a valid person name');
      return;
    }
    if (form.TYPE === 'COMPANY' && !validateBusinessName(form.NAME)) {
      setFormError('Please enter a valid company name');
      return;
    }
    if (form.EMAIL.trim() && !validateEmail(form.EMAIL)) {
      setFormError('Please enter a valid email address');
      return;
    }
    // Validate phone only if user has entered something beyond the prefix
    const phoneEntered = form.PHONE.trim() && form.PHONE.trim() !== defaultPrefix.trim();
    if (phoneEntered && !validatePhone(form.PHONE, phoneRegion)) {
      setPhoneError(getPhoneValidationMessage(phoneRegion));
      return;
    }
    setSaving(true);
    const normalizedPhone = phoneEntered ? normalizePhone(form.PHONE, phoneRegion) : '';
    await onSave({
      ...form,
      NAME: form.NAME.trim(),
      EMAIL: normalizeEmail(form.EMAIL),
      PHONE: normalizedPhone,
    });
    setSaving(false);
  }

  return (
    <Modal title={partner ? "Edit Partner" : "Add Partner"} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Type toggle */}
          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Type</label>
            <div className="flex gap-2 mt-1">
              {["PERSON", "COMPANY"].map((t) => (
                <button key={t} type="button" onClick={() => set("TYPE", t)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${form.TYPE === t ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}>
                  {t === "PERSON" ? "Person" : "Company"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Name *</label>
            <input type="text" value={form.NAME} onChange={(e) => set("NAME", e.target.value)} required
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder={form.TYPE === "PERSON" ? "John Smith" : "ABC Services Pvt Ltd"} autoComplete={form.TYPE === "PERSON" ? "name" : "organization"} />
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Category *</label>
            <select value={form.CATEGORY} onChange={(e) => set("CATEGORY", e.target.value)} required
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">Select category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Phone</label>
            <input type="tel" value={form.PHONE}
              onChange={(e) => { set("PHONE", e.target.value); setPhoneError(""); }}
              className={`w-full mt-1 px-3 py-2 border rounded-lg text-sm ${phoneError ? 'border-red-400' : ''}`}
              placeholder={phoneExample} inputMode="tel" autoComplete="tel" />
            {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Email</label>
            <input type="email" value={form.EMAIL} onChange={(e) => set("EMAIL", e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="contact@example.com" autoComplete="email" />
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Address</label>
            <input type="text" value={form.ADDRESS} onChange={(e) => set("ADDRESS", e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="Shop No. 5, Main Road..." />
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase font-medium">Description</label>
            <textarea value={form.DESCRIPTION} onChange={(e) => set("DESCRIPTION", e.target.value)} rows={2}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm resize-none" placeholder="Brief description of services..." />
          </div>

          <button type="submit" disabled={saving}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 active:bg-blue-700">
            {saving ? "Saving..." : partner ? "Update Partner" : "Add Partner"}
          </button>
          {formError && <p className="text-xs text-red-500 text-center">{formError}</p>}
      </form>
    </Modal>
  );
}
